import axios from "../utils/axios";

const PRESERVE_UPPER_TOKENS = new Set([
  "ACT",
  "NSW",
  "NT",
  "QLD",
  "SA",
  "TAS",
  "VIC",
  "WA",
  "DC",
  "RAAF",
  "HMAS",
  "ANU",
  "BC",
  "MC",
]);

const normalizeStateCode = (value) => {
  const v = String(value || "").trim().toUpperCase();
  if (!v) return "";
  // Accept common AU state/territory abbreviations
  if (["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"].includes(v)) return v;
  return v;
};

const normalizePostcode = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  // AU postcodes are 4 digits; still return whatever we can if API sends longer
  return digits.slice(0, 4);
};

const smartTitleCaseToken = (token) => {
  const t = String(token || "").trim();
  if (!t) return "";
  const upper = t.toUpperCase();
  if (PRESERVE_UPPER_TOKENS.has(upper)) return upper;

  // If it's not all-caps, keep as-is (API may already send correct casing)
  const looksAllCaps = t === upper && /[A-Z]/.test(t);
  if (!looksAllCaps) return t;

  // Handle apostrophes: O'CONNOR / O’MALLEY
  const parts = t.split(/['’]/g);
  const normalizedParts = parts
    .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : ""))
    .filter((p) => p !== "");

  // Reconstruct using the original apostrophe char if present
  const apostropheMatch = t.match(/['’]/);
  const joiner = apostropheMatch ? apostropheMatch[0] : "'";
  return normalizedParts.join(joiner);
};

const smartTitleCase = (value) => {
  const str = String(value || "").trim();
  if (!str) return "";

  // Split by whitespace and hyphens but keep hyphens in place
  return str
    .split(" ")
    .map((word) => {
      if (!word) return "";
      return word
        .split("-")
        .map((tok) => smartTitleCaseToken(tok))
        .join("-");
    })
    .filter(Boolean)
    .join(" ");
};

const normalizeSuburbsPayload = (payload) => {
  if (!payload) return [];
  const list = Array.isArray(payload) ? payload : (payload?.data || payload?.suburbs || payload?.results);
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (typeof item === "string") return smartTitleCase(item);
      return smartTitleCase(item?.name || item?.suburb || item?.label || item?.value || "");
    })
    .filter(Boolean);
};

const extractSuburbsForState = (payload, stateCode) => {
  const state = String(stateCode || "").trim().toUpperCase();
  if (!payload) return payload;

  // Handle common shapes:
  // - { code, state, suburbs: [...] }
  // - [{ code, state, suburbs: [...] }, ...]
  // - { data: { ... } } or { data: [ ... ] }
  const unwrapped =
    Array.isArray(payload?.data) || (payload?.data && typeof payload.data === "object")
      ? payload.data
      : payload;

  if (unwrapped && Array.isArray(unwrapped?.suburbs)) return unwrapped.suburbs;

  if (Array.isArray(unwrapped)) {
    const match = unwrapped.find((x) => {
      const code = String(x?.code || x?.stateCode || x?.state || "").trim().toUpperCase();
      return code === state;
    });
    if (match && Array.isArray(match?.suburbs)) return match.suburbs;
  }

  return unwrapped;
};

export async function fetchSuburbsByState(stateCode) {
  const state = String(stateCode || "").trim();
  if (!state) return [];

  // Expected backend: GET /location/suburbs?state=NSW
  // You can override the path via REACT_APP_SUBURBS_ENDPOINT (defaults to "/location/suburbs")
  const rawEndpoint = process.env.REACT_APP_SUBURBS_ENDPOINT || "/location/suburbs";
  const endpoint = rawEndpoint.startsWith("/") ? rawEndpoint : `/${rawEndpoint}`;

  // Use the shared axios instance so it automatically picks up `REACT_APP_BASE_URL`.
  const url = endpoint.replace(/^\//, "");
  const response = await axios.get(url, { params: { state } });
  const data = extractSuburbsForState(response?.data, state);
  const suburbs = normalizeSuburbsPayload(data);

  // de-dupe + sort
  return Array.from(new Set(suburbs)).sort((a, b) => a.localeCompare(b));
}

const normalizeSuburbLookupPayload = (payload) => {
  const unwrapped =
    payload?.data && (typeof payload.data === "object" || Array.isArray(payload.data))
      ? payload.data
      : payload;

  const obj = Array.isArray(unwrapped) ? unwrapped[0] : unwrapped;
  if (!obj || typeof obj !== "object") return { state: "", postalCode: "" };

  const state =
    normalizeStateCode(obj?.state || obj?.stateCode || obj?.region || obj?.province);
  const postalCode =
    normalizePostcode(obj?.postalCode || obj?.postcode || obj?.postCode || obj?.zip || obj?.zipCode);

  return { state, postalCode };
};

export async function fetchStateAndPostalCodeBySuburb(suburb, stateHint) {
  const s = smartTitleCase(String(suburb || "").trim());
  if (!s) return { state: "", postalCode: "" };

  // You can override the path via REACT_APP_SUBURB_LOOKUP_ENDPOINT (defaults to "/location/lookup")
  const rawEndpoint = process.env.REACT_APP_SUBURB_LOOKUP_ENDPOINT || "/location/lookup";
  const endpoint = rawEndpoint.startsWith("/") ? rawEndpoint : `/${rawEndpoint}`;
  // Use the shared axios instance so it automatically picks up `REACT_APP_BASE_URL`.
  const url = endpoint.replace(/^\//, "");

  const params = { suburb: s };
  const hint = normalizeStateCode(stateHint);
  if (hint) params.state = hint;

  const response = await axios.get(url, { params });
  return normalizeSuburbLookupPayload(response?.data);
}

